/**
 * {@include ../README.md}
 * @packageDocumentation
 */

// Components
export {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './components/accordion';

export {Alert, AlertDescription, AlertTitle} from './components/alert';

export {AspectRatio} from './components/aspect-ratio';

export {Badge, badgeVariants, type BadgeProps} from './components/badge';

export {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './components/breadcrumb';

export {Button, buttonVariants, type ButtonProps} from './components/button';

export {Calendar, type CalendarProps} from './components/calendar';

export {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from './components/card';

export {Checkbox} from './components/checkbox';

export {
  Combobox,
  type ComboboxContentProps,
  type ComboboxItemProps,
  type ComboboxRootProps,
  type ComboboxTriggerProps,
} from './components/combobox';
export {
  useCombobox,
  type UseComboboxOptions,
  type UseComboboxReturn,
} from './hooks/useCombobox';

export {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './components/collapsible';

export {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
} from './components/command';

export {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuGroup,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuPortal,
  ContextMenuRadioGroup,
  ContextMenuRadioItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from './components/context-menu';

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from './components/dialog';

export {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHandle,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
} from './components/drawer';

export {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from './components/dropdown-menu';

export {EditableText} from './components/editable-text';

export {ErrorBoundary} from './components/error-boundary';

export {ErrorPane} from './components/error-pane';

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useFormField,
} from './components/form';

export {Input} from './components/input';

export {
  SettingsPanelHeader,
  type SettingsPanelHeaderProps,
} from './components/settings-panel-header';

export {Label} from './components/label';

export {
  ModifierScrollOverlay,
  type ModifierScrollOverlayProps,
} from './components/modifier-scroll-overlay';

export {
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarPortal,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
} from './components/menu-bar';

export {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from './components/pagination';

export {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverTrigger,
} from './components/popover';

export {ProgressModal} from './components/progress-modal';

export {Progress} from './components/progress';

export {RadioGroup, RadioGroupItem} from './components/radio-group';

export {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from './components/resizable';
export type {
  ResizablePanelOrientation,
  ResizablePanelHandle,
} from './components/resizable';

export {RunButton, type RunButtonProps} from './components/run-button';

export {
  TabStrip,
  type TabDescriptor,
  type TabStripDndMode,
  type TabStripDragData,
  type TabStripProps,
} from './components/tab-strip';

export {ScrollableRow} from './components/scrollable-row';

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from './components/select';

export {ScrollArea, ScrollBar} from './components/scroll-area';

export {Separator} from './components/separator';

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
} from './components/sheet';

export {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from './components/sidebar';

export {SkeletonPane} from './components/skeleton-pane';

export {Skeleton} from './components/skeleton';

export {Slider} from './components/slider';

export {SpinnerPane} from './components/spinner-pane';

export {Spinner} from './components/spinner';

export {Switch} from './components/switch';

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from './components/table';

export {Tabs, TabsContent, TabsList, TabsTrigger} from './components/tabs';

export {Textarea} from './components/textarea';

export {ThemeSwitch} from './components/theme-switch';

export {ToggleGroup, ToggleGroupItem} from './components/toggle-group';

export {Toggle, toggleVariants} from './components/toggle';

export {CopyButton, type CopyButtonProps} from './components/copy-button';

export {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './components/tooltip';

export {Tree, type TreeNodeData} from './components/tree';

// Hooks
export {toast, type ExternalToast} from 'sonner';

export {
  useAspectRatioDimensions,
  type Dimensions,
  type UseAspectRatioDimensionsProps,
} from './hooks/useAspectRatioDimensions';

export {
  useDisclosure,
  type UseDisclosureReturnValue,
} from './hooks/useDisclosure';

export {useDebounce} from './hooks/useDebounce';
export {useDebouncedCallback} from './hooks/useDebouncedCallback';
export {useDebouncedValue} from './hooks/useDebouncedValue';

export {useIsMobile} from './hooks/use-mobile';
export {useRelativeCoordinates} from './hooks/useRelativeCoordinates';

export {
  useAutoResizeTextarea,
  type UseAutoResizeTextareaOptions,
  type UseAutoResizeTextareaResult,
} from './hooks/useAutoResizeTextarea';

// Utilities
export {resolveFontSizeClass, type FontSizeToken} from './lib/fontSize';
export {cn} from './lib/utils';

// Theme
export {
  DEFAULT_THEME,
  DEFAULT_THEME_STORAGE_KEY,
  getResolvedTheme,
  getTheme,
  getThemePreference,
  ThemeProvider,
  useTheme,
  type ResolvedTheme,
  type Theme,
} from './theme/theme-provider';

// Re-export from Radix
export {Slot} from '@radix-ui/react-slot';

export {Toaster} from './components/sonner';

export {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from './components/hover-card';
